import { useTranslation } from 'react-i18next'
import { KatexRenderer } from '@quebon/shared-learning/utils'
import { Button3D } from '@/components/learner/ui/Button3D'
import {
  displayCorrectAnswer,
  hasDisplayableCorrectAnswer,
} from './QuestionResultCard'
import {
  PartialCreditGradeToggle,
  type PartialCreditGrade,
} from './PartialCreditGradeToggle'

export interface SubjectiveGrade {
  isCorrect: boolean
  partialCredit: PartialCreditGrade
}

/** 서술형 타입(explanation) — 정오답이 주관적이라 자동 채점하지 않는다. */
export function isSubjectiveQuizType(snapshot: unknown): boolean {
  if (!snapshot || typeof snapshot !== 'object') return false
  return (snapshot as { type?: unknown }).type === 'explanation'
}

interface Props {
  disabled?: boolean
  correctAnswer?: unknown
  questionSnapshot?: unknown
  allowPartialCredit?: boolean
  onChange: (grade: SubjectiveGrade) => void
}

// 서술형 답안 제출과 채점을 한 번에 처리하는 우측 하단 3D 버튼.
export function SubjectiveGradeSelect({
  disabled,
  correctAnswer,
  questionSnapshot,
  allowPartialCredit = false,
  onChange,
}: Props) {
  const { t } = useTranslation('coaching')
  const showCorrectAnswer = hasDisplayableCorrectAnswer(correctAnswer)

  return (
    <div className="mt-6 flex flex-col gap-3">
      {showCorrectAnswer ? (
        <div
          role="note"
          data-testid="subjective-correct-answer"
          aria-label={t('override.modelAnswer')}
          className="rounded-lg border border-border bg-muted/50 p-4"
        >
          <p className="mb-1 text-xs font-semibold text-muted-foreground">
            {t('override.modelAnswer')}
          </p>
          <div className="text-base font-medium text-foreground whitespace-pre-wrap">
            <KatexRenderer text={displayCorrectAnswer(correctAnswer, questionSnapshot)} />
          </div>
        </div>
      ) : null}

      <div
        data-testid="subjective-grade-select"
        role="group"
        aria-label={t('override.subjectiveAriaLabel')}
        className="flex justify-end gap-3"
      >
        {allowPartialCredit ? (
          <PartialCreditGradeToggle
            disabled={disabled}
            onChange={(partialCredit) => onChange({
              isCorrect: partialCredit === 1,
              partialCredit,
            })}
          />
        ) : (
          <>
            <Button3D
              variant="lightblue"
              data-testid="grade-correct"
              disabled={disabled}
              onClick={() => onChange({ isCorrect: true, partialCredit: 1 })}
            >
              {t('override.toCorrect')}
            </Button3D>
            <Button3D
              variant="orange"
              data-testid="grade-incorrect"
              disabled={disabled}
              onClick={() => onChange({ isCorrect: false, partialCredit: 0 })}
            >
              {t('override.toIncorrect')}
            </Button3D>
          </>
        )}
      </div>
    </div>
  )
}
